import { CurrencyConfigRes, RestrictedCountryRes } from "src/store/features/api/generated";
import { toIso3166Alpha2 } from "src/utils/countryCodes";

/**
 * A token's currency config can carry a `restrictedCountries` list. A user
 * whose address country matches one of those entries must not be able to buy
 * or deposit that token. The backend is the source of truth and enforces this;
 * these helpers let the UI block the action proactively and show a friendly
 * message before the request is ever made.
 */

type ConfigWithRestrictions = Pick<CurrencyConfigRes, "code" | "restrictedCountries"> & {
  restrictedCountries?: RestrictedCountryRes[];
};

const normalise = (value?: string): string => (value ?? "").trim().toUpperCase();

/**
 * Returns the matching restricted-country entry when the user's country is
 * restricted for the given token, otherwise `undefined`.
 *
 * Mirrors the backend check (`apps/backend/src/app/utils/restricted-country.util.ts`):
 * both the stored restricted-country `id`/`name` and the user's country are
 * normalised to ISO 3166-1 alpha-2 before comparison, so a user address of
 * "GB" matches a Greenwich entry like `{ id: "77", name: "United Kingdom" }`.
 * When either side can't be resolved to a code we fall back to a
 * case-insensitive raw compare rather than silently passing the check.
 */
export const findRestrictedCountryMatch = (
  currencyConfig: ConfigWithRestrictions | undefined | null,
  userCountry: string | undefined | null,
): RestrictedCountryRes | undefined => {
  const restricted = currencyConfig?.restrictedCountries;
  if (!restricted?.length || !userCountry) return undefined;

  const userIso = toIso3166Alpha2(userCountry);
  const userFallback = normalise(userCountry);

  return restricted.find((entry) => {
    const restrictedIso = toIso3166Alpha2(entry?.id) ?? toIso3166Alpha2(entry?.name);
    if (restrictedIso && userIso) {
      return restrictedIso === userIso;
    }
    return normalise(entry?.id) === userFallback || normalise(entry?.name) === userFallback;
  });
};

/**
 * User-friendly message shown when a token is restricted for the user's
 * country, or `null` when the token is allowed.
 */
export const getRestrictedCountryMessage = (
  currencyConfig: ConfigWithRestrictions | undefined | null,
  userCountry: string | undefined | null,
): string | null => {
  const match = findRestrictedCountryMatch(currencyConfig, userCountry);
  if (!match) return null;

  const token = currencyConfig?.code ?? "This token";
  const countryLabel = match.name || match.id || userCountry;
  return `${token} is not supported for ${countryLabel}.`;
};

/**
 * Backend block message format: "<CODE> token is restricted for country - <NAME>".
 * Converts it to the friendlier UI wording, or returns `null` when the message
 * is not a restricted-country error (so callers can fall back to the original).
 */
export const toFriendlyRestrictedMessage = (raw?: string | null): string | null => {
  if (!raw) return null;
  const match = raw.match(/^(.+?) token is restricted for country - (.+)$/);
  if (!match) return null;
  const [, code, country] = match;
  return `${code} is not supported for ${country}.`;
};

/**
 * Whether the given token is restricted for the user's country.
 */
export const isTokenRestrictedForCountry = (
  currencyConfig: ConfigWithRestrictions | undefined | null,
  userCountry: string | undefined | null,
): boolean => findRestrictedCountryMatch(currencyConfig, userCountry) !== undefined;
